import React, { useState } from "react";
import Grid from "@mui/material/Grid2";
import { Box, MenuItem, Typography, Button, useMediaQuery } from "@mui/material";
import validator from "validator";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { defaultVariables } from "../../../../config";
import { industries } from "../../../../config/Permissions";
import LoadingButton from "../../../components/elements/LoadingButton";
import RayaField from "../../../components/elements/fields/RayaField";
import MobileField from "../../../components/elements/fields/MobileField";
import AddressChooserOnMap from "../../../components/address/map/AddressChooserOnMap";
import LocateMeButton from "../../../components/address/map/LocateMeButton";
import StaticMap from "../../../components/address/map/StaticMap";
import SubTitle from "../../../components/elements/SubTitle";
import { theme } from "../../../../config/design/theme";

const MarketingVendorForm = ({ session, onSuccess }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [submitLoading, setSubmitLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [location, setLocation] = useState({});
    const [mapLevel, setMapLevel] = useState(1);
    const [taxFile, setTaxFile] = useState(null);
    const [errors, setErrors] = useState({});
    const [fields, setFields] = useState({
        owner_first_name: "",
        owner_last_name: "",
        shop_name: "",
        industry: "restaurants",
        tc_kimlik_no: "",
        address_text: "",
        vergi_numarasi: "",
        mobile: "",
        country: null,
        telephone: "",
        email: "",
    });

    const marketingHeaders = () => ({
        Accept: "application/json",
        "X-Marketing-Session": session.session_token,
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFields((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const setMobile = (country, mobile) => {
        setFields((prev) => ({ ...prev, country, mobile }));
        setErrors((prev) => ({ ...prev, mobile: "" }));
    };

    const validateFields = () => {
        const newErrors = {};
        let valid = true;

        ["owner_first_name", "owner_last_name", "shop_name", "address_text", "vergi_numarasi", "email"].forEach((key) => {
            if (!fields[key]?.trim()) {
                valid = false;
                newErrors[key] = t("emptyField");
            }
        });

        if (!validator.isEmail(fields.email)) {
            valid = false;
            newErrors.email = t("wrongEmail");
        }

        if (!fields.tc_kimlik_no || fields.tc_kimlik_no.length !== 11 || !validator.isNumeric(fields.tc_kimlik_no)) {
            valid = false;
            newErrors.tc_kimlik_no = t("marketingInvalidTcKimlik");
        }

        if (!validator.isNumeric(fields.mobile) || fields.mobile.length < 9) {
            valid = false;
            newErrors.mobile = t("wrongMobile");
        }

        if (!location?.coordinates?.lat || !location?.coordinates?.lng) {
            valid = false;
            newErrors.location = t("marketingSelectLocation");
        }

        setErrors(newErrors);
        return valid;
    };

    const handleTaxUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadLoading(true);
        const data = new FormData();
        data.append("image", file);
        data.append("rep_id", session.rep_id);
        data.append("session_token", session.session_token);

        try {
            const response = await axios.post(
                `${defaultVariables.url}/marketing/leads/upload-tax-document`,
                data,
                { headers: marketingHeaders() }
            );
            setTaxFile(response.data);
            enqueueSnackbar(t("detailsSet"), { variant: "success" });
        } catch (error) {
            enqueueSnackbar(t("detailsSetFailed"), { variant: "error" });
        } finally {
            setUploadLoading(false);
            e.target.value = "";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);

        if (!validateFields()) {
            setSubmitLoading(false);
            return;
        }

        let mobile = Number(fields.mobile).toString();
        mobile = fields.country?.phonecode + mobile;

        const payload = {
            rep_id: session.rep_id,
            session_token: session.session_token,
            owner_first_name: fields.owner_first_name,
            owner_last_name: fields.owner_last_name,
            shop_name: fields.shop_name,
            industry: fields.industry,
            tc_kimlik_no: fields.tc_kimlik_no,
            address_text: fields.address_text,
            address: location.address,
            province: location.province,
            country: location.country,
            lat: location.coordinates?.lat,
            lng: location.coordinates?.lng,
            vergi_numarasi: fields.vergi_numarasi,
            mobile,
            code: fields.country?.phonecode,
            telephone: fields.telephone,
            email: fields.email,
            tax_document_file_id: taxFile?.id || null,
        };

        try {
            await axios.post(`${defaultVariables.url}/marketing/leads/submit`, payload, {
                headers: marketingHeaders(),
            });
            sessionStorage.removeItem("marketing_rep_id");
            sessionStorage.removeItem("marketing_session_token");
            onSuccess();
        } catch (error) {
            enqueueSnackbar(t("detailsSetFailed"), { variant: "error" });
        } finally {
            setSubmitLoading(false);
        }
    };

    const applyLocatedAddress = (loc) => {
        if (!loc?.coordinates) return;
        setLocation(loc);
        if (loc.address) {
            setFields((prev) => ({ ...prev, address_text: loc.address }));
        }
        setErrors((prev) => ({ ...prev, location: "" }));
    };

    const confirmMapLocation = () => {
        if (location.country && location.country !== "Türkiye") {
            enqueueSnackbar(t("onlyTurkiye"), { variant: "warning" });
            return;
        }
        if (location.address && !fields.address_text) {
            setFields((prev) => ({ ...prev, address_text: location.address }));
        }
        setMapLevel(2);
    };

    return (
        <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
                <Grid item size={12}>
                    <Typography color="textSecondary">
                        {t("marketingWelcomeRep")}: <strong>{session.rep_name}</strong>
                    </Typography>
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                    <RayaField
                        required
                        fullWidth
                        variant="outlined"
                        name="owner_first_name"
                        label={t("first_name")}
                        value={fields.owner_first_name}
                        onChange={handleChange}
                        error={!!errors.owner_first_name}
                        helperText={errors.owner_first_name}
                    />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <RayaField
                        required
                        fullWidth
                        variant="outlined"
                        name="owner_last_name"
                        label={t("last_name")}
                        value={fields.owner_last_name}
                        onChange={handleChange}
                        error={!!errors.owner_last_name}
                        helperText={errors.owner_last_name}
                    />
                </Grid>
                <Grid item size={12}>
                    <RayaField
                        required
                        fullWidth
                        variant="outlined"
                        name="shop_name"
                        label={t("marketingShopName")}
                        value={fields.shop_name}
                        onChange={handleChange}
                        error={!!errors.shop_name}
                        helperText={errors.shop_name}
                    />
                </Grid>
                <Grid item size={12}>
                    <RayaField
                        required
                        fullWidth
                        select
                        variant="outlined"
                        name="industry"
                        label={t("industry")}
                        value={fields.industry}
                        onChange={handleChange}
                    >
                        {industries.map((item) => (
                            <MenuItem key={item} value={item}>
                                {t(item)}
                            </MenuItem>
                        ))}
                    </RayaField>
                </Grid>
                <Grid item size={12}>
                    <RayaField
                        required
                        fullWidth
                        variant="outlined"
                        name="tc_kimlik_no"
                        label={t("marketingTcKimlik")}
                        value={fields.tc_kimlik_no}
                        onChange={handleChange}
                        error={!!errors.tc_kimlik_no}
                        helperText={errors.tc_kimlik_no}
                        inputProps={{ maxLength: 11 }}
                    />
                </Grid>

                <Grid item size={12}>
                    <SubTitle align="left">{t("marketingLocationTitle")}</SubTitle>
                    {mapLevel === 1 ? (
                        <>
                            <Box sx={{ mb: 1.5 }}>
                                <LocateMeButton skipAuth setLocation={applyLocatedAddress} />
                            </Box>
                            <AddressChooserOnMap
                                setLocation={setLocation}
                                location={location}
                                height={isMobile ? 280 : 400}
                            />
                            {!!errors.location && (
                                <Typography color="error" variant="caption">
                                    {errors.location}
                                </Typography>
                            )}
                            {!!location.address && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {location.address}
                                </Typography>
                            )}
                            <Button
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={confirmMapLocation}
                                disabled={!location.coordinates}
                            >
                                {t("submitInfo")}
                            </Button>
                        </>
                    ) : (
                        <>
                            <StaticMap height={200} coordinates={location.coordinates} />
                            {!!location.address && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {location.address}
                                </Typography>
                            )}
                            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                                <Button startIcon={<ArrowLeft />} onClick={() => setMapLevel(1)}>
                                    {t("back")}
                                </Button>
                                <LocateMeButton skipAuth setLocation={applyLocatedAddress} />
                            </Box>
                        </>
                    )}
                </Grid>

                <Grid item size={12}>
                    <RayaField
                        required
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        name="address_text"
                        label={t("marketingStoreAddress")}
                        value={fields.address_text}
                        onChange={handleChange}
                        error={!!errors.address_text}
                        helperText={errors.address_text}
                    />
                </Grid>

                <Grid item size={12}>
                    <RayaField
                        required
                        fullWidth
                        variant="outlined"
                        name="vergi_numarasi"
                        label={t("marketingVergiNumarasi")}
                        value={fields.vergi_numarasi}
                        onChange={handleChange}
                        error={!!errors.vergi_numarasi}
                        helperText={errors.vergi_numarasi}
                    />
                </Grid>

                <Grid item size={12}>
                    <SubTitle align="left">{t("marketingVergiLevhasi")}{t("optionalField")}</SubTitle>
                    <Button variant="outlined" component="label" disabled={uploadLoading}>
                        {taxFile ? t("marketingVergiLevhasiUploaded") : t("marketingVergiLevhasiUpload")}
                        <input type="file" accept="image/*,application/pdf" capture="environment" hidden onChange={handleTaxUpload} />
                    </Button>
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                    <MobileField setMobile={setMobile} error={errors.mobile} required />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <RayaField
                        fullWidth
                        variant="outlined"
                        name="telephone"
                        label={t("marketingTelephone")}
                        value={fields.telephone}
                        onChange={handleChange}
                    />
                </Grid>
                <Grid item size={12}>
                    <RayaField
                        required
                        fullWidth
                        variant="outlined"
                        name="email"
                        label={t("email")}
                        value={fields.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                    />
                </Grid>

                <Grid item size={12}>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        color="secondary"
                        fullWidth
                        loading={submitLoading}
                    >
                        {t("marketingSubmitLead")}
                    </LoadingButton>
                </Grid>
            </Grid>
        </form>
    );
};

export default MarketingVendorForm;
